import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle,
  Database,
  TrendingUp,
  PieChart,
  Warehouse,
  Sparkles,
  ArrowLeft } from
"lucide-react";
import { useHubSpotForm } from "@/hooks/useHubSpotForm";
import SEO from "@/components/SEO";

const LOGO_URL =
"https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/0t2i84sr_Logo.png";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const TemplatesPage = () => {
  useHubSpotForm({
    containerId: "templates-page-form",
    portalId: "47937401",
    formId: "8133ea7b-48fe-4bae-948a-b59aab9e4b26"
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="templates-page">
      <SEO
        title="Gratis Power BI skabeloner til Business Central & e-conomic | Valubi"
        description="Tilmeld dig nyhedsbrevet og få gratis Power BI skabeloner der forbinder direkte til Business Central eller e-conomic. Skal det skaleres, bygger vi data warehouse og AI-assisteret BI til fast pris."
        path="/skabeloner"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Gratis Power BI skabeloner",
          "url": "https://valubi.dk/skabeloner",
          "inLanguage": "da-DK",
          "isPartOf": { "@id": "https://valubi.dk/#website" },
          "about": ["Power BI", "Business Central", "e-conomic"]
        }} />

      {/* Minimal top bar */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="templates-logo-link">
            <img src={LOGO_URL} alt="Valubi" className="h-10 w-auto" />
          </Link>
          <Link
            to="/"
            className="hidden sm:inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors"
            data-testid="templates-back-link">

            <ArrowLeft size={16} />
            Tilbage til forsiden
          </Link>
        </div>
      </header>

      {/* Hero + form */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
          {/* Left column — value prop */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="lg:col-span-3">

            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-100 rounded-full mb-6">
              <Sparkles className="text-teal-600" size={16} />
              <span className="text-teal-700 font-manrope font-semibold text-xs uppercase tracking-wider">
                Gratis Power BI skabeloner
              </span>
            </div>

            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-slate-900 mb-6 leading-tight">
              Få dine gratis Power BI{" "}
              <span className="text-teal-600">skabeloner</span>
            </h1>

            <p className="text-slate-600 text-lg mb-8 leading-relaxed">
              Tilmeld dig nyhedsbrevet, så sender vi dig et link, hvor du kan
              downloade skabelonerne. De forbinder direkte til Business Central
              eller e-conomic.
            </p>

            <div className="space-y-4 mb-10">
              <div
                className="flex items-start gap-3"
                data-testid="benefit-templates">

                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center mt-0.5">
                  <PieChart className="text-teal-600" size={20} />
                </div>
                <div>
                  <h3 className="font-manrope font-semibold text-slate-900 mb-1">
                    Færdige Power BI skabeloner
                  </h3>
                  <p className="text-slate-600 text-sm">
                    Salg, Finans, Projekt og Lager (kun BC). Som du ser dem i videoerne.
                  </p>
                </div>
              </div>

              <div
                className="flex items-start gap-3"
                data-testid="benefit-erp">

                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center mt-0.5">
                  <Database className="text-teal-600" size={20} />
                </div>
                <div>
                  <h3 className="font-manrope font-semibold text-slate-900 mb-1">
                    Forbinder direkte til dit ERP
                  </h3>
                  <p className="text-slate-600 text-sm">
                    Business Central eller e-conomic. Ingen mellemled, ingen
                    Excel-eksport.
                  </p>
                </div>
              </div>

              <div
                className="flex items-start gap-3"
                data-testid="benefit-fast">

                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center mt-0.5">
                  <TrendingUp className="text-teal-600" size={20} />
                </div>
                <div>
                  <h3 className="font-manrope font-semibold text-slate-900 mb-1">
                    Klar på under 30 minutter
                  </h3>
                  <p className="text-slate-600 text-sm">
                    Download, åbn, indsæt dit firmanavn, og du er kørende.
                  </p>
                </div>
              </div>

              <div
                className="flex items-start gap-3"
                data-testid="benefit-scale">

                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center mt-0.5">
                  <Warehouse className="text-teal-600" size={20} />
                </div>
                <div>
                  <h3 className="font-manrope font-semibold text-slate-900 mb-1">
                    Skal det skaleres senere?
                  </h3>
                  <p className="text-slate-600 text-sm">
                    Vi bygger et rigtigt data warehouse eller en AI-assisteret
                    BI-platform oven på. Til fast pris.
                  </p>
                </div>
              </div>
            </div>

            <div
              className="bg-teal-50 border border-teal-100 rounded-2xl p-6 mb-10"
              data-testid="templates-ladder-card">

              <h3 className="font-manrope font-semibold text-slate-900 mb-2">
                Godt at vide om templates
              </h3>
              <p className="text-slate-700 text-sm leading-relaxed mb-3">
                Templates er den letteste start. De har ingen historik, ingen
                rene definitioner og er ikke AI-parate. Det løser vores
                betalte pakker: et standard data warehouse med rene,
                historiserede data, eller den AI-assisterede BI-platform, hvor
                jeres eget team laver ændringer via en AI-agent.
              </p>
              <Link
                to="/#priser"
                className="inline-flex items-center gap-2 text-teal-700 font-semibold text-sm hover:text-teal-800 transition-colors"
                data-testid="templates-page-pricing-link">

                Se pakker og priser
              </Link>
            </div>

            <div
              className="hidden lg:block bg-white border border-slate-100 rounded-2xl p-6"
              data-testid="templates-trust-card">

              <p className="text-slate-700 text-sm leading-relaxed mb-3">
                <strong className="font-manrope text-slate-900">
                  Ingen spam, ingen skjulte gebyrer.
                </strong>{" "}
                Du får ét link til skabelonerne, og nyhedsbrevet kommer kun
                sjældent. Du kan afmelde med ét klik.
              </p>
              <div className="flex items-center gap-2 text-teal-700 text-sm font-medium">
                <CheckCircle size={16} />
                100% gratis
              </div>
            </div>
          </motion.div>

          {/* Right column — form */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ delay: 0.15 }}
            className="lg:col-span-2 lg:sticky lg:top-8">

            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-100 shadow-sm">
              <div
                id="templates-page-form"
                className="min-h-[300px]"
                data-testid="templates-page-hubspot-form" />

            </div>
          </motion.div>
        </div>
      </main>

      {/* Minimal footer */}
      <footer className="border-t border-slate-100 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Valubi ApS — CVR 45291745
          </p>
          <Link
            to="/"
            className="text-slate-500 hover:text-teal-600 text-sm transition-colors"
            data-testid="templates-footer-home-link">

            valubi.dk
          </Link>
        </div>
      </footer>
    </div>);

};

export default TemplatesPage;
